import { BoundNumberField, BoundTextField, FieldGroup, FormLines } from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { InputMaybe } from "src/generated/graphqlTypes";
import { useReaction } from "src/hooks";
import { handleReasonNoteValidationRules } from "../utils";

type BoundOptionPricingConfigFieldsProps = {
  postCutoffDesiredMarginInBasisPoints: FieldState<InputMaybe<number>>;
  preCutoffDesiredMarginInBasisPoints: FieldState<InputMaybe<number>>;
  selectionFeeInCents: FieldState<InputMaybe<number>>;
  minimumPriceInCents: FieldState<InputMaybe<number>>;
  reasonNote: FieldState<InputMaybe<string>>;
  isStructural?: boolean;
  disabled?: boolean | string;
};

export function BoundOptionPricingConfigFields({
  postCutoffDesiredMarginInBasisPoints,
  preCutoffDesiredMarginInBasisPoints,
  selectionFeeInCents,
  minimumPriceInCents,
  reasonNote,
  isStructural = false,
  disabled = false,
}: BoundOptionPricingConfigFieldsProps) {
  // if any of the option pricing config fields have changed, then clear the reason note and requrie it to be filled out
  const optionPricingConfig = [
    postCutoffDesiredMarginInBasisPoints,
    preCutoffDesiredMarginInBasisPoints,
    selectionFeeInCents,
    minimumPriceInCents,
  ];

  useReaction(
    () => ({
      isDirty: optionPricingConfig.map((field) => field.dirty).some((val) => val),
      reasonNoteValue: reasonNote.originalValue,
      hasExistingValues: optionPricingConfig.map((field) => field.originalValue).some((val) => !!val),
    }),
    ({ isDirty, reasonNoteValue, hasExistingValues }) =>
      handleReasonNoteValidationRules(isDirty, reasonNote, reasonNoteValue, hasExistingValues),
    [reasonNote, optionPricingConfig],
  );

  return (
    <FormLines width="full">
      <FieldGroup>
        <BoundNumberField
          label={isStructural ? "Margin" : "Pre-Cutoff Margin"}
          type="basisPoints"
          field={preCutoffDesiredMarginInBasisPoints}
          disabled={disabled}
          placeholder="40%"
        />
        {isStructural ? (
          <></>
        ) : (
          <BoundNumberField
            label="Post-Cutoff Margin"
            type="basisPoints"
            field={postCutoffDesiredMarginInBasisPoints}
            disabled={disabled}
          />
        )}
        <BoundNumberField label="Selection Fee" field={selectionFeeInCents} type="cents" disabled={disabled} />
        <BoundNumberField label="Minimum Price" field={minimumPriceInCents} type="cents" disabled={disabled} />
      </FieldGroup>
      <BoundTextField label="Update Note" field={reasonNote} disabled={disabled} />
    </FormLines>
  );
}
